import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { analyticsAPI, sessionAPI } from '../../services/api';
import wsService from '../../services/websocket';
import { useAuthStore, useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable/DataTable';
import { pageHidden } from '../../components/Sidebar/menuConfig';
import usePermission from '../../hooks/usePermission';
import { guestIdentity, guestDetails } from '../../utils/guest';
import SignInsChart from './SignInsChart';
import OnlineNowCard from './OnlineNowCard';
import BandwidthCard from './BandwidthCard';
import './Dashboard.css';

const sinceLabel = (date) => {
    if (!date) return 'never seen';
    const mins = Math.max(0, Math.round((Date.now() - new Date(date)) / 60000));
    if (mins < 60) return `${mins} min ago`;
    if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} days ago`;
};

const AdminDashboard = () => {
    const admin = useAuthStore((state) => state.admin);
    // A card or shortcut leads only where the sidebar does: not to a page Access Control
    // hides from this admin (MainLayout loads it).
    const access = useAuthStore((state) => state.access);
    const canOpen = (path) => !pageHidden(path, access);
    const selectedBranch = useBranchStore((state) => state.selectedBranch);
    const { canDelete: canDisconnect } = usePermission('sessions');

    const [loading, setLoading]               = useState(true);
    const [dashboardData, setDashboardData]   = useState(null);
    const [activeSessions, setActiveSessions] = useState([]);
    const [failedLogins, setFailedLogins]     = useState([]);
    const [revenueData, setRevenueData]       = useState(null);
    const [dateFilter, setDateFilter]         = useState('today');
    const [lastUpdated, setLastUpdated]       = useState(new Date());
    const [currentTime, setCurrentTime]       = useState(new Date());
    const [refreshing, setRefreshing]         = useState(false);

    /* ── Live clock ── */
    useEffect(() => {
        const t = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    /* ── WebSocket ── */
    useEffect(() => {
        wsService.connect();
        const onMsg = (data) => {
            if (data.type === 'STATS_UPDATE') toast.info('Live update received');
        };
        wsService.on('message', onMsg);
        return () => { wsService.off('message', onMsg); wsService.disconnect(); };
    }, []);

    /* ── Data fetching ── */
    const fetchDashboard = useCallback(async () => {
        try {
            const r = await analyticsAPI.getDashboard(selectedBranch?._id, dateFilter);
            setDashboardData(r.data.data);
        } catch (_) {}
    }, [selectedBranch, dateFilter]);

    const fetchActiveSessions = useCallback(async () => {
        try {
            const r = await sessionAPI.getActive(selectedBranch?._id);
            setActiveSessions(r.data.data.sessions || []);
        } catch (_) {}
    }, [selectedBranch]);

    // Ends the session on the gateway (CoA); the guest signs in through the portal again.
    const disconnectSession = async (session, who) => {
        if (!window.confirm(`Disconnect ${who}? They will have to sign in again.`)) return;
        try {
            await sessionAPI.disconnect(session._id);
            toast.success(`${who} disconnected`);
            fetchActiveSessions();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not disconnect the session');
        }
    };

    // The last 4 hours of failed WiFi-login attempts (guest never got online), from the
    // same AuthorizationSession records the Auth Logs page reads — there is no
    // password field here to show: this app's guest logins are OTP/PMS/voucher, never
    // a stored password, so that column is always "—".
    const fetchFailedLogins = useCallback(async () => {
        try {
            const from = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
            const r = await api.get('/auth-logs', { params: { status: 'FAILED', branchId: selectedBranch?._id, from, limit: 200 } });
            setFailedLogins(r.data.data || []);
        } catch (_) {}
    }, [selectedBranch]);

    const fetchRevenue = useCallback(async () => {
        try {
            const r = await analyticsAPI.getRevenue(selectedBranch?._id, dateFilter);
            setRevenueData(r.data.data);
        } catch (_) {}
    }, [selectedBranch, dateFilter]);

    const fetchAll = useCallback(async (showLoader = true) => {
        if (showLoader) setLoading(true);
        else setRefreshing(true);
        await Promise.all([fetchDashboard(), fetchActiveSessions(), fetchFailedLogins(), fetchRevenue()]);
        setLastUpdated(new Date());
        setLoading(false);
        setRefreshing(false);
    }, [fetchDashboard, fetchActiveSessions, fetchFailedLogins, fetchRevenue]);

    useEffect(() => { fetchAll(true); }, [selectedBranch, dateFilter]);

    useEffect(() => {
        const t = setInterval(() => fetchAll(false), 30000);
        return () => clearInterval(t);
    }, [fetchAll]);

    /* ── Computed values ── */
    const activeCount      = dashboardData?.overview?.activeSessions ?? activeSessions.length;
    const bandwidthUsed    = dashboardData?.dataUsage?.total || 0;
    const revenueToday     = revenueData?.totalRevenue || dashboardData?.revenue?.today || 0;
    const newSignups       = dashboardData?.overview?.newUsers || 0;
    const voucherSales     = revenueData?.vouchers || dashboardData?.revenue?.vouchers || 0;
    // Routers (online = RADIUS heard from them in 5 min) + APs / switches on Device Status.
    const devices          = dashboardData?.devices || { online: 0, offline: 0, routers: { online: 0, offline: 0 }, accessPoints: { online: 0, offline: 0 } };
    const userTypeDistribution       = dashboardData?.userTypeDistribution || [];
    const membershipTierDistribution = dashboardData?.membershipTierDistribution || [];
    const topConsumers     = dashboardData?.topConsumers || [];
    const todayUpgrades    = dashboardData?.todayUpgrades || 0;
    const expiredPlansCount = dashboardData?.expiredPlansCount || 0;

    // How long a session has been up, in seconds — it is still running, so from its start.
    const sessionSeconds = (s) => (s.startTime ? Math.max(0, Math.floor((currentTime - new Date(s.startTime)) / 1000)) : 0);
    const avgSessionTime = (() => {
        if (!activeSessions.length) return 0;
        const total = activeSessions.reduce((s, x) => s + sessionSeconds(x), 0);
        return Math.round(total / activeSessions.length / 60);
    })();

    const formatBytes = (b) => {
        if (!b) return '0 MB';
        if (b >= 1099511627776) return `${(b / 1099511627776).toFixed(1)} TB`;
        if (b >= 1073741824)    return `${(b / 1073741824).toFixed(1)} GB`;
        if (b >= 1048576)       return `${(b / 1048576).toFixed(1)} MB`;
        return `${(b / 1024).toFixed(1)} KB`;
    };

    // The big number and its unit apart ("85.5" "GB"), as the card shows them.
    const bytesParts = (b) => {
        for (const [unit, size] of [['TB', 1099511627776], ['GB', 1073741824], ['MB', 1048576], ['KB', 1024]]) {
            if (b >= size) return { value: (b / size).toFixed(1), unit };
        }
        return { value: '0', unit: 'MB' };
    };

    const formatDuration = (s) => {
        if (!s) return '0m';
        const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    const fmtDay   = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const fmtClock = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    /* ── Date filter options ── */
    const dateOptions = [
        { value: 'today', label: 'Today' },
        { value: 'week',  label: 'This Week' },
        { value: 'month', label: 'This Month' },
    ];
    const periodLabel = dateOptions.find(o => o.value === dateFilter)?.label || 'Today';

    /* ── The four headline cards ── */
    const dataParts = bytesParts(bandwidthUsed);
    const kpis = [
        {
            key: 'sessions', tone: 'navy', icon: 'fa-user', label: 'Active Sessions', to: '/sessions',
            value: activeCount.toLocaleString(), note: 'Guests connected',
        },
        {
            key: 'online', tone: 'green', icon: 'fa-wifi', label: 'Online Devices', to: '/nas',
            value: devices.online.toLocaleString(), note: 'Routers / APs',
            hint: `${devices.routers.online} router${devices.routers.online === 1 ? '' : 's'} · ${devices.accessPoints.online} access point${devices.accessPoints.online === 1 ? '' : 's'} online`,
        },
        {
            key: 'data', tone: 'navy', icon: 'fa-arrows-up-down', label: 'Total Data Usage', to: '/session-report',
            value: dataParts.value, unit: dataParts.unit, note: 'Download + Upload', hint: `Download + upload, ${periodLabel.toLowerCase()}`,
        },
        {
            key: 'offline', tone: 'red', icon: 'fa-plug-circle-xmark', label: 'Offline Devices', to: '/device-status',
            value: devices.offline.toLocaleString(), note: devices.offline ? 'Devices offline' : 'All devices online',
            hint: `${devices.routers.offline} router${devices.routers.offline === 1 ? '' : 's'} · ${devices.accessPoints.offline} access point${devices.accessPoints.offline === 1 ? '' : 's'} offline`,
        },
    ];

    /* ── Below the cards ── */
    const last24h   = dashboardData?.last24h || [];
    const rooms     = dashboardData?.rooms || null;
    const topPlans  = dashboardData?.topPlans || [];
    const offlineList = devices.offlineList || [];
    const occupancy = rooms?.total ? Math.round((rooms.occupied / rooms.total) * 100) : 0;
    const topPlanMax = Math.max(1, ...topPlans.map(p => p.sessions || 0));
    const shortcuts = [
        { to: '/vouchers',       icon: 'fa-ticket-alt',      label: 'Vouchers' },
        { to: '/sessions',       icon: 'fa-satellite-dish',  label: 'Live sessions' },
        ...(rooms ? [{ to: '/rooms', icon: 'fa-bed', label: 'Rooms' }] : []),
        { to: '/nas',            icon: 'fa-server',          label: 'Routers' },
        { to: '/plans',          icon: 'fa-layer-group',     label: 'Plans' },
        { to: '/device-status',  icon: 'fa-heart-pulse',     label: 'Device status' },
    ].filter(s => canOpen(s.to));

    if (loading) return (
        <div className="ad-loading">
            <div className="ad-spinner"></div>
            <p>Loading dashboard...</p>
        </div>
    );

    return (
        <div className="ad-wrapper">

            {/* ── Header ── */}
            <div className="td-header">
                <div>
                    <h1 className="td-title">Dashboard</h1>
                    <p className="td-subtitle">
                        Welcome back, {admin?.name?.split(' ')[0] || 'Admin'}! Here's what's happening with your Wi-Fi network.
                    </p>
                </div>
                <div className="td-header-right">
                    <div className="ad-date-filter" role="group" aria-label="Period">
                        {dateOptions.map(o => (
                            <button
                                key={o.value}
                                className={`ad-filter-btn ${dateFilter === o.value ? 'active' : ''}`}
                                onClick={() => setDateFilter(o.value)}
                                aria-pressed={dateFilter === o.value}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <button
                        className="td-refresh"
                        onClick={() => fetchAll(false)}
                        title={`Refresh — updated ${fmtClock(lastUpdated)}`}
                        aria-label="Refresh"
                    >
                        <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                    </button>
                    <div className="td-datetime">
                        <i className="far fa-calendar"></i>
                        <span>{fmtDay(currentTime)}</span>
                        <span className="td-datetime-time">{fmtClock(currentTime)}</span>
                    </div>
                </div>
            </div>

            {/* ── The four headline cards ── */}
            <div className="td-kpi-grid">
                {kpis.map((k) => {
                    const linked = canOpen(k.to);
                    const Card = linked ? Link : 'div';
                    return (
                        <Card key={k.key} {...(linked ? { to: k.to } : {})} className={`td-kpi td-tone-${k.tone}`} title={k.hint}>
                            <span className="td-kpi-icon"><i className={`fas ${k.icon}`}></i></span>
                            <span className="td-kpi-label">{k.label}</span>
                            <span className="td-kpi-value">
                                {k.value}{k.unit && <span className="td-kpi-unit">{k.unit}</span>}
                            </span>
                            <span className="td-kpi-foot">
                                <span>{k.note}</span>
                                {linked && <i className="fas fa-arrow-right" aria-hidden="true"></i>}
                            </span>
                        </Card>
                    );
                })}
            </div>

            {/* ── 4 Metric Cards ── */}
            <div className="ad-metrics-grid">
                <div className="ad-metric-card">
                    <div className="ad-metric-icon ad-icon-purple">
                        <i className="fas fa-clock"></i>
                    </div>
                    <div className="ad-metric-body">
                        <div className="ad-metric-label">Avg. Session Time</div>
                        <div className="ad-metric-value">{avgSessionTime}m</div>
                    </div>
                </div>
                <div className="ad-metric-card">
                    <div className="ad-metric-icon ad-icon-green">
                        <i className="fas fa-indian-rupee-sign"></i>
                    </div>
                    <div className="ad-metric-body">
                        <div className="ad-metric-label">Revenue · {periodLabel}</div>
                        <div className="ad-metric-value">₹{revenueToday.toLocaleString('en-IN', { minimumFractionDigits: 0 })}</div>
                    </div>
                </div>
                <div className="ad-metric-card">
                    <div className="ad-metric-icon ad-icon-pink">
                        <i className="fas fa-user-plus"></i>
                    </div>
                    <div className="ad-metric-body">
                        <div className="ad-metric-label">New Sign-ups</div>
                        <div className="ad-metric-value">{newSignups}</div>
                    </div>
                </div>
                <div className="ad-metric-card">
                    <div className="ad-metric-icon ad-icon-green">
                        <i className="fas fa-ticket-alt"></i>
                    </div>
                    <div className="ad-metric-body">
                        <div className="ad-metric-label">Voucher Sales</div>
                        <div className="ad-metric-value">₹{voucherSales.toLocaleString('en-IN')}</div>
                    </div>
                </div>
            </div>

            {/* ── Sign-ins by hour + top plans | what needs attention + shortcuts ── */}
            <div className="td-panels">
                <div className="td-side">
                    <SignInsChart buckets={last24h} formatBytes={formatBytes} />

                    <div className="ad-card">
                        <div className="ad-card-header">
                            <div className="ad-card-title">
                                <i className="fas fa-layer-group"></i>
                                <h2>Top plans · {periodLabel}</h2>
                            </div>
                        </div>
                        {topPlans.length === 0 ? (
                            <div className="td-allgood td-muted"><span>No sessions in this period yet</span></div>
                        ) : (
                            <ul className="td-plans">
                                {topPlans.map((p, i) => (
                                    <li key={`${p.plan}-${i}`}>
                                        <div className="td-plan-row">
                                            <span className="td-plan-name">{p.plan}</span>
                                            <span className="td-plan-meta"><strong>{p.sessions}</strong> sessions · {formatBytes(p.dataUsage)}</span>
                                        </div>
                                        <div className="td-plan-track"><div className="td-plan-fill" style={{ width: `${Math.round((p.sessions / topPlanMax) * 100)}%` }}></div></div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="td-side">
                    <div className="ad-card">
                        <div className="ad-card-header">
                            <div className="ad-card-title">
                                <i className="fas fa-triangle-exclamation"></i>
                                <h2>Needs attention</h2>
                            </div>
                            {canOpen('/device-status') && <Link className="td-link" to="/device-status">View all</Link>}
                        </div>
                        {offlineList.length === 0 ? (
                            <div className="td-allgood">
                                <i className="fas fa-circle-check"></i>
                                <span>All routers and access points are online</span>
                            </div>
                        ) : (
                            <ul className="td-attention">
                                {offlineList.map((d, i) => (
                                    <li key={`${d.kind}-${d.name}-${i}`}>
                                        <i className={`fas ${d.kind === 'router' ? 'fa-server' : 'fa-wifi'} td-attention-icon`}></i>
                                        <div className="td-attention-body">
                                            <span className="td-attention-name">{d.name}</span>
                                            <span className="td-attention-meta">{d.kind === 'router' ? 'Router' : 'Access point / switch'} · last seen {sinceLabel(d.since)}</span>
                                        </div>
                                        <span className="td-badge-offline"><i className="fas fa-circle-xmark"></i> Offline</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {rooms && (
                        <div className="ad-card">
                            <div className="ad-card-header">
                                <div className="ad-card-title">
                                    <i className="fas fa-bed"></i>
                                    <h2>Room occupancy</h2>
                                </div>
                                {canOpen('/rooms') && <Link className="td-link" to="/rooms">Rooms</Link>}
                            </div>
                            <div className="td-occupancy">
                                <div className="td-occupancy-figure">{occupancy}<span>%</span></div>
                                <div className="td-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={occupancy} aria-label="Rooms occupied">
                                    <div className="td-meter-fill" style={{ width: `${occupancy}%` }}></div>
                                </div>
                                <div className="td-occupancy-legend">
                                    <span><strong>{rooms.occupied}</strong> occupied</span>
                                    <span><strong>{rooms.available}</strong> available</span>
                                    <span><strong>{rooms.total}</strong> rooms</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {shortcuts.length > 0 && (
                        <div className="ad-card">
                            <div className="ad-card-header">
                                <div className="ad-card-title">
                                    <i className="fas fa-bolt"></i>
                                    <h2>Quick actions</h2>
                                </div>
                            </div>
                            <div className="td-shortcuts">
                                {shortcuts.map(s => (
                                    <Link key={s.to} to={s.to} className="td-shortcut">
                                        <i className={`fas ${s.icon}`}></i>
                                        <span>{s.label}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Sessions Table ── */}
            <div className="ad-card">
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-satellite-dish"></i>
                        <h2>Recent Sessions</h2>
                        {activeSessions.length > 0 && (
                            <span className="ad-live-badge">
                                <span className="ad-pulse-dot"></span>
                                {activeSessions.length} Live
                            </span>
                        )}
                    </div>
                    <button className="ad-refresh-small" onClick={fetchActiveSessions}>
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                </div>

                {activeSessions.length === 0 ? (
                    <div className="ad-empty">
                        <i className="fas fa-satellite-dish"></i>
                        <h4>No active sessions</h4>
                        <p>Sessions will appear here once users connect</p>
                    </div>
                ) : (
                    <div className="ad-table-wrap">
                        <table className="ad-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Device</th>
                                    <th>Started</th>
                                    <th>Duration</th>
                                    <th>Bandwidth</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeSessions.slice(0, 10).map((session, i) => {
                                    // The guest (name, else room, else mobile — utils/guest), their
                                    // device as the portal saw it (else its MAC), and the session's
                                    // own figures: it is still running, so its length is since it started.
                                    const who = guestIdentity(session.userId)?.label || 'Unknown';
                                    const details = guestDetails(session.userId);
                                    const device = session.deviceInfo?.deviceType || session.deviceInfo?.os || '';
                                    const bytes = session.dataUsage?.total || (session.dataUsage?.upload || 0) + (session.dataUsage?.download || 0);
                                    return (
                                    <tr key={session._id || i}>
                                        <td>
                                            <div className="ad-user-cell">
                                                <div className="ad-user-avatar">
                                                    {who.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="ad-user-name">
                                                    {who}
                                                    {details.length > 0 && <small>{details.join(' · ')}</small>}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ad-device-cell" title={session.deviceMac || undefined}>
                                                <i className={`fas ${/mobile|phone|tablet|android|ios/i.test(device) ? 'fa-mobile-alt' : 'fa-laptop'}`}></i>
                                                <span>{device || session.deviceMac || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td className="ad-time-cell">
                                            {session.startTime
                                                ? new Date(session.startTime).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : 'N/A'}
                                        </td>
                                        <td className="ad-time-cell">
                                            {formatDuration(sessionSeconds(session))}
                                        </td>
                                        <td className="ad-bw-cell">
                                            {formatBytes(bytes)}
                                        </td>
                                        <td>
                                            <span className={`ad-status ${session.status === 'active' ? 'ad-active' : 'ad-pending'}`}>
                                                <span className="ad-status-dot"></span>
                                                {session.status === 'active' ? 'Active' : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="ad-row-actions">
                                                {canOpen('/sessions') && (
                                                    <Link className="ad-btn-view" title="All sessions" to="/sessions">
                                                        <i className="fas fa-eye"></i>
                                                    </Link>
                                                )}
                                                {canDisconnect && (
                                                    <button type="button" className="ad-btn-disc" title="Disconnect"
                                                        onClick={() => disconnectSession(session, who)}>
                                                        <i className="fas fa-power-off"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Online now, gateway by gateway (MikroTik live, Ruijie and others from accounting) ── */}
            <OnlineNowCard branchId={selectedBranch?._id} />

            {/* ── Bandwidth now: use, each gateway's link, the latest speed changes ── */}
            <BandwidthCard branchId={selectedBranch?._id} />

            {/* ── Online Users ── */}
            <div className="ad-card" style={{ marginTop: '1.5rem' }}>
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-users"></i>
                        <h2>Online Users</h2>
                    </div>
                </div>
                <div style={{ padding: '1.125rem 1.375rem' }}>
                    <DataTable
                        columns={[
                            {
                                label: 'User Id', field: 'userId',
                                render: (r) => <span className="td-guest"><strong>{r.userId}</strong>{r.userDetails && <small>{r.userDetails}</small>}</span>,
                                searchValue: (r) => `${r.userId} ${r.userDetails}`,
                            },
                            { label: 'Start Time', field: 'startTime' },
                            { label: 'Plan Name', field: 'planName' },
                            { label: 'IP Addr', field: 'ip' },
                            { label: 'Data (MB)', field: 'dataMb' },
                            { label: 'Minutes', field: 'minutes' },
                            { label: 'Location', field: 'location' },
                            { label: 'MAC ID', field: 'macId' },
                        ]}
                        rows={activeSessions.map((session) => ({
                            id: session._id,
                            userId: guestIdentity(session.userId)?.label || 'Unknown',
                            userDetails: guestDetails(session.userId).join(' · '),
                            startTime: session.startTime ? new Date(session.startTime).toLocaleString('en-IN') : '—',
                            planName: session.planId?.name || '—',
                            ip: session.framedIpAddress || '—',
                            dataMb: ((session.dataUsage?.total || 0) / 1048576).toFixed(1),
                            minutes: session.startTime ? Math.max(0, Math.floor((currentTime - new Date(session.startTime)) / 60000)) : 0,
                            location: session.location?.zone || session.location?.apName || '—',
                            macId: session.deviceMac || '—',
                        }))}
                    />
                </div>
            </div>

            {/* ── Failed Login Attempts ── */}
            <div className="ad-card" style={{ marginTop: '1.5rem' }}>
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-user-lock"></i>
                        <h2>Failed Login Attempts (Past 4 hours)</h2>
                    </div>
                </div>
                <div style={{ padding: '1.125rem 1.375rem' }}>
                    <DataTable
                        defaultPageSize={50}
                        columns={[
                            { label: 'Login Time', field: 'loginTime' },
                            { label: 'User Id', field: 'userId' },
                            { label: 'MAC ID', field: 'macId' },
                            { label: 'Password', field: 'password', sortable: false, searchValue: () => '' },
                            { label: 'NASID', field: 'nasId' },
                            { label: 'Reason', field: 'reason' },
                        ]}
                        rows={failedLogins.map((log) => ({
                            id: log._id,
                            loginTime: log.createdAt ? new Date(log.createdAt).toLocaleString('en-IN') : '—',
                            userId: log.guest?.guestName || log.guest?.roomNumber || log.guest?.mobile || log.guest?.bookingId || '—',
                            macId: log.network?.mac || '—',
                            password: <span title="Not stored — guest WiFi login here uses OTP/PMS/voucher, never a password">—</span>,
                            nasId: log.nas?.nasIdentifier || log.nas?.nasId || '—',
                            reason: log.result?.errorMessage || 'Authorization denied',
                        }))}
                    />
                </div>
            </div>

            {/* ── User Type / Membership / Upgrades / Top Consumers ── */}
            <div className="ad-stats-grid" style={{ marginTop: '1.5rem' }}>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip ad-strip-purple"></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon ad-icon-purple"><i className="fas fa-arrow-up"></i></div>
                        <span className="ad-stat-label">Today's Upgrades</span>
                    </div>
                    <div className="ad-stat-value">{todayUpgrades}</div>
                </div>
                <div className="ad-stat-card">
                    <div className="ad-stat-strip" style={{ background: '#dc2626' }}></div>
                    <div className="ad-stat-top">
                        <div className="ad-stat-icon" style={{ background: '#dc262618', color: '#dc2626' }}><i className="fas fa-exclamation-triangle"></i></div>
                        <span className="ad-stat-label">Expired Plans</span>
                    </div>
                    <div className="ad-stat-value">{expiredPlansCount}</div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginTop: '1.25rem' }}>
                {userTypeDistribution.length > 0 && (
                    <div className="ad-card">
                        <div className="ad-card-header">
                            <div className="ad-card-title"><i className="fas fa-users-cog"></i><h2>User Type Distribution</h2></div>
                        </div>
                        <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                            {userTypeDistribution.map((u, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', textTransform: 'capitalize' }}>
                                    <span>{u.type}</span>
                                    <strong>{u.count}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {membershipTierDistribution.length > 0 && (
                    <div className="ad-card">
                        <div className="ad-card-header">
                            <div className="ad-card-title"><i className="fas fa-medal"></i><h2>Membership Tiers</h2></div>
                        </div>
                        <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                            {membershipTierDistribution.map((t, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                                    <span>{t.tier}</span>
                                    <strong>{t.count}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {topConsumers.length > 0 && (
                    <div className="ad-card">
                        <div className="ad-card-header">
                            <div className="ad-card-title"><i className="fas fa-chart-line"></i><h2>Top Data Consumers</h2></div>
                        </div>
                        <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                            {topConsumers.slice(0, 5).map((u, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                                    <span>{u.name} <span style={{ color: '#9ca3af', fontWeight: 400 }}>{u.mobile}</span></span>
                                    <strong>{formatBytes(u.dataUsed)}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default AdminDashboard;
